import { UIConfig, RecordingPermissions } from '../types/whiteboardProps';
import { GetQbTestForEditOutput } from '../lib/api/types';
import { UserInfo } from '../store/useUserStore';
import { WebViewExternalProps, mapWebViewUIConfig } from '../integrations/webview';

interface TenantSettings {
  // İleride tenant bazlı ayarlar eklenebilir
  defaultRecordingQuality?: 'low' | 'medium' | 'high';
  maxRecordingDuration?: number;
  allowPublicRecordings?: boolean;
}

/**
 * Test verilerinden UI config oluşturur
 * Backend'den test ayarları gelene kadar bu mantık kullanılacak
 */
export function buildUIConfigFromTest(
  testData: GetQbTestForEditOutput,
  userInfo: UserInfo | null,
  tenantSettings?: TenantSettings
): UIConfig {
  const isDenemeTesti = testData.qbTest?.examType !== 1; // 1: Deneme testi, diğerleri normal testlerdir
  
  // Kullanıcı rolünü belirle (şimdilik basit bir mantık)
  // TODO: userInfo'dan gerçek rol bilgisi alınmalı
  const userRole = determineUserRole(userInfo);
  
  // Test tipine ve kullanıcı rolüne göre recording permissions oluştur
  const recordingPermissions = buildRecordingPermissions(
    isDenemeTesti,
    userRole,
    tenantSettings
  );
  
  // Temel UI ayarları
  const baseConfig: UIConfig = {
    // Toolbar ve navigasyon
    showToolbar: true,
    showQuestionNav: true,
    showTimer: isDenemeTesti, // Deneme testlerinde timer göster
    
    // Çizim araçları
    allowDrawing: true,
    allowEraser: true,
    allowShapes: !isDenemeTesti || userRole === 'teacher', // Deneme testlerinde öğrenciler için şekil araçları kapalı
    allowHighlighter: true,
    readOnly: false,
    
    // Optik form - URL'den kontrol ediliyor
    showOpticalForm: true,
    opticalAutoDetect: !isDenemeTesti, // Deneme testlerinde otomatik algılama kapalı
    
    // Export yetkisi
    allowExport: !isDenemeTesti && (userRole === 'teacher' || userRole === 'admin'),
    
    // Animasyon ayarları - sınav modunda kapalı
    animationsEnabled: !isDenemeTesti,
    hoverEffectsEnabled: !isDenemeTesti,
    selectionAnimationDuration: isDenemeTesti ? 0 : 200,
    
    // Ses geri bildirimi
    voiceFeedbackEnabled: false, // Varsayılan olarak kapalı
    
    // Optik cevap ayarları
    showOpticalAnswerSubmit: true,
    opticalAnswerAutoSave: true,
    opticalAnswerAutoSaveInterval: 30, // 30 saniye
    allowOpticalAnswerClear: !isDenemeTesti || userRole === 'teacher',
    
    // Recording permissions
    recordingPermissions,
    
    // Solution viewing ayarları
    allowSolutionViewing: !isDenemeTesti || userRole === 'teacher',
    solutionVisibilityMode: determineSolutionVisibilityMode(userRole, isDenemeTesti),
    allowOwnSolutionManagement: !isDenemeTesti,
    maxSolutionsToShow: userRole === 'teacher' ? 50 : 10,
    showSolutionAuthorInfo: userRole === 'teacher',
    showSolutionStats: userRole === 'teacher',
  };
  
  return baseConfig;
}

/**
 * Kullanıcı rolünü belirle
 */
function determineUserRole(userInfo: UserInfo | null): 'student' | 'teacher' | 'admin' | 'viewer' {
  return 'admin';
}

/**
 * Recording permissions oluştur
 */
function buildRecordingPermissions(
  isDenemeTesti: boolean,
  userRole: string,
  tenantSettings?: TenantSettings
): RecordingPermissions {
  // Deneme testi için kısıtlı yetkiler
  if (isDenemeTesti) {
    return {
      canRecord: true, // Tüm kullanıcılar kayıt yapabilir
      canViewRecordings: true,
      recordingVisibility: {
        showOwnRecordings: true,
        showTenantRecordings: true,
        showPublicRecordings: false,
      },
      recordingManagement: {
        canDeleteOwnRecordings: true,
        canExportOwnRecordings: true,
        canShareRecordings: true,
        canMakeRecordingsPublic: true,
      },
      recordingSettings: {
        maxDuration: 600, // 5 dakika
        quality: 'high',
        autoSave: true,
        autoName: true,
      },
    };
  }
  
  // Normal testler için rol bazlı yetkiler
  switch (userRole) {
    case 'teacher':
    case 'admin':
      return {
        canRecord: true,
        canViewRecordings: true,
        recordingVisibility: {
          showOwnRecordings: true,
          showTenantRecordings: true,
          showPublicRecordings: true,
        },
        recordingManagement: {
          canDeleteOwnRecordings: true,
          canExportOwnRecordings: true,
          canShareRecordings: true,
          canMakeRecordingsPublic: true,
        },
        recordingSettings: {
          maxDuration: tenantSettings?.maxRecordingDuration || 1800, // 30 dakika
          quality: tenantSettings?.defaultRecordingQuality || 'high',
          autoSave: false,
          autoName: true,
        },
      };
      
    case 'student':
      return {
        canRecord: true,
        canViewRecordings: true,
        recordingVisibility: {
          showOwnRecordings: true,
          showTenantRecordings: true,
          showPublicRecordings: true,
        },
        recordingManagement: {
          canDeleteOwnRecordings: true,
          canExportOwnRecordings: false,
          canShareRecordings: true,
          canMakeRecordingsPublic: false,
        },
        recordingSettings: {
          maxDuration: 600, // 10 dakika
          quality: 'medium',
          autoSave: false,
          autoName: true,
        },
      };
      
    case 'viewer':
    default:
      return {
        canRecord: false,
        canViewRecordings: true,
        recordingVisibility: {
          showOwnRecordings: false,
          showTenantRecordings: false,
          showPublicRecordings: true,
        },
        recordingManagement: {
          canDeleteOwnRecordings: false,
          canExportOwnRecordings: false,
          canShareRecordings: false,
          canMakeRecordingsPublic: false,
        },
      };
  }
}

/**
 * Solution visibility mode belirle
 */
function determineSolutionVisibilityMode(
  userRole: string,
  isDenemeTesti: boolean
): 'own' | 'tenant' | 'test' | 'public' {
  if (isDenemeTesti) {
    return 'own'; // Deneme testlerinde sadece kendi çözümleri
  }
  
  switch (userRole) {
    case 'teacher':
    case 'admin':
      return 'tenant'; // Öğretmenler tenant seviyesinde görebilir
    case 'student':
      return 'test'; // Öğrenciler test seviyesinde görebilir
    default:
      return 'public'; // Misafirler sadece public görebilir
  }
}

/**
 * Varsayılan UI config (test verisi yoksa kullanılır)
 */
export function getDefaultUIConfig(): UIConfig {
  return {
    showToolbar: true,
    showQuestionNav: true,
    showTimer: false,
    allowDrawing: true,
    allowEraser: true,
    allowShapes: true,
    allowHighlighter: true,
    readOnly: false,
    showOpticalForm: false,
    allowExport: false,
    animationsEnabled: true,
    hoverEffectsEnabled: true,
    voiceFeedbackEnabled: false,
    opticalAutoDetect: true,
    showOpticalAnswerSubmit: true,
    opticalAnswerAutoSave: true,
    opticalAnswerAutoSaveInterval: 30,
    allowOpticalAnswerClear: true,
    recordingPermissions: {
      canRecord: true,
      canViewRecordings: true,
      recordingVisibility: {
        showOwnRecordings: true,
        showTenantRecordings: true,
        showPublicRecordings: true,
      },
      recordingManagement: {
        canDeleteOwnRecordings: true,
        canExportOwnRecordings: false,
        canShareRecordings: true,
        canMakeRecordingsPublic: false,
      },
      recordingSettings: {
        quality: 'medium',
        autoSave: false,
        autoName: true,
      },
    },
    allowSolutionViewing: false,
    solutionVisibilityMode: 'own',
    allowOwnSolutionManagement: true,
    maxSolutionsToShow: 10,
    showSolutionAuthorInfo: true,
    showSolutionStats: true,
  };
}

/**
 * Build UI config from WebView external props
 */
export function buildUIConfigFromWebView(
  externalProps: WebViewExternalProps,
  userInfo: UserInfo | null
): UIConfig {
  // If WebView provides UI config, use it with defaults
  if (externalProps.uiConfig) {
    return mapWebViewUIConfig(externalProps.uiConfig);
  }
  
  // Otherwise build from test config if available
  if (externalProps.testConfig) {
    const testData: Partial<GetQbTestForEditOutput> = {
      qbTest: {
        id: externalProps.testConfig.testId,
        title: externalProps.testConfig.testName,
        examType: externalProps.testConfig.studyTask.params.examType,
      }
    };
    
    return buildUIConfigFromTest(testData as GetQbTestForEditOutput, userInfo);
  }
  
  // Default config
  return getDefaultUIConfig();
}